import { MetricId } from '../../../../../../api/types-graphql';
import { formatMetricValue } from '../../../../../../utils-display';
import { SortTypes } from '../../../../../types';
import {
  TableData,
  TableViewHeader,
  TableViewRow,
} from '../../../../tableview/types';
import {
  ComponentStateManager,
  DataVizContainerCommonUtils,
} from '../../../common/types';
import {
  MultiMetricComparisonVizData,
  MultiMetricComparisonVizDataResponse,
  MultiMetricComparisonVizExternalProps,
} from '../../types';
import {
  MultiMetricComparisonTableViewActions,
  MultiMetricComparisonTableViewState,
  MultiMetricComparisonTableViewUiComponentProps,
} from './types';

/**
 * Transforms multi-metric comparison data into table format.
 * Each row represents a metric with its name and value.
 */
const getTableDataFromMultiMetricResponse = (
  response: MultiMetricComparisonVizDataResponse,
  metricDetails: MultiMetricComparisonVizData['metricDetails'],
  selectedSortOrder?: SortTypes
): TableData => {
  const { metricValues } = response;

  // Sort metric values based on sort order
  let sortedMetricValues = [...metricValues];
  if (selectedSortOrder) {
    sortedMetricValues = sortedMetricValues.sort((a, b) => {
      if (selectedSortOrder === SortTypes.ASC) {
        return a.label.localeCompare(b.label);
      } else if (selectedSortOrder === SortTypes.DESC) {
        return b.label.localeCompare(a.label);
      } else if (selectedSortOrder === SortTypes.HIGH) {
        return b.value - a.value;
      } else if (selectedSortOrder === SortTypes.LOW) {
        return a.value - b.value;
      }
      return 0;
    });
  }

  // Get the first metric's format type for value formatting
  // Assumption: all metrics have the same format type
  const firstMetricDetails = metricValues[0]
    ? metricDetails[metricValues[0].metric.value as unknown as MetricId]
    : undefined;

  const headers: TableViewHeader[] = [{ value: 'Metric' }, { value: 'Value' }];

  const rows: TableViewRow[] = sortedMetricValues.map((mv) => {
    const formattedValue = firstMetricDetails
      ? formatMetricValue(mv.value, firstMetricDetails.formatType)
      : String(mv.value);

    return {
      key: String(mv.metric.value),
      title: mv.label,
      cells: [
        {
          key: `${mv.metric.value}-value`,
          value: formattedValue,
        },
      ],
    };
  });

  return {
    headers,
    rows,
  };
};

export const multiMetricComparisonTableViewDataToUiComponentProps = (
  data: MultiMetricComparisonVizData,
  externalProps: MultiMetricComparisonVizExternalProps,
  componentStateManager: ComponentStateManager<
    MultiMetricComparisonTableViewState,
    MultiMetricComparisonTableViewActions
  >,
  _commonUtils: DataVizContainerCommonUtils
): MultiMetricComparisonTableViewUiComponentProps => {
  const { fullScreenHandle } = externalProps;
  const { dataResponse, titleConfig, metricDetails } = data;
  const { selectedSortOrder } = componentStateManager.state.displayState;

  const tableData = getTableDataFromMultiMetricResponse(
    dataResponse,
    metricDetails,
    selectedSortOrder
  );

  const displayConfig = {
    ...componentStateManager.state.displayState,
    ...componentStateManager.actions.displayActions,
  };

  return {
    fullScreenHandle,
    displayConfig,
    titleConfig,
    tableData,
  };
};
